<template>
  <DrawerModal
    :is-open="isOpen"
    title="profile.my.notification.title"
    :has-changes="isFormChanged"
    :is-loading="isLoading"
    :is-disabled="isLoading"
    @close="handleClose"
    @submit="saveNotifications"
  >
    <div class="profile-notifications">
      <div v-if="pushPreferences" class="general-sections">
        <GamSwitch
          v-bind="pushEnabled()"
          v-model:checked="isPushDeliveryEnabled"
          :disabled="!isSystemPushEnabled"
          :loading="isSavingToggle"
        />

        <div v-if="!isSystemPushEnabled" class="system-disabled-notice">
          {{ localize('profile.my.notification.system_disabled') }}
        </div>

        <CategorySection
          :title="localize('profile.my.notification.category.boards')"
          :category="NotificationCategory.BOARDS"
          :preference="pushPreferences[NotificationCategory.BOARDS]"
          :sub-type-labels="boardsSubTypes"
          :disabled="areCategoriesDisabled"
          @update="updatePushCategory"
        />

        <CategorySection
          :title="localize('profile.my.notification.category.irl_play_confirmed')"
          :category="NotificationCategory.IRL_PLAY_CONFIRMED"
          :preference="pushPreferences[NotificationCategory.IRL_PLAY_CONFIRMED]"
          :sub-type-labels="irlPlayConfirmedSubTypes"
          :disabled="areCategoriesDisabled"
          @update="updatePushCategory"
        />

        <CategorySection
          :title="localize('profile.my.notification.category.irl_play_changed')"
          :category="NotificationCategory.IRL_PLAY_CHANGED"
          :preference="pushPreferences[NotificationCategory.IRL_PLAY_CHANGED]"
          :sub-type-labels="irlPlayChangedSubTypes"
          :disabled="areCategoriesDisabled"
          @update="updatePushCategory"
        />

        <CategorySection
          :title="localize('profile.my.notification.category.irl_play_proposed')"
          :category="NotificationCategory.IRL_PLAY_PROPOSED"
          :preference="pushPreferences[NotificationCategory.IRL_PLAY_PROPOSED]"
          :sub-type-labels="irlPlayProposedSubTypes"
          :disabled="areCategoriesDisabled"
          @update="updatePushCategory"
        />

        <CategorySection
          :title="localize('profile.my.notification.category.meet_anyways')"
          :category="NotificationCategory.MEET_ANYWAYS"
          :preference="pushPreferences[NotificationCategory.MEET_ANYWAYS]"
          :sub-type-labels="{}"
          :simple="true"
          :disabled="areCategoriesDisabled"
          @update="updatePushCategory"
        />

        <CategorySection
          :title="localize('profile.my.notification.category.challenges')"
          :category="NotificationCategory.CHALLENGES"
          :preference="pushPreferences[NotificationCategory.CHALLENGES]"
          :sub-type-labels="challengeSubTypes"
          :disabled="areCategoriesDisabled"
          @update="updatePushCategory"
        />

        <CategorySection
          :title="localize('profile.my.notification.category.invitations')"
          :category="NotificationCategory.INVITATIONS"
          :preference="pushPreferences[NotificationCategory.INVITATIONS]"
          :sub-type-labels="invitationSubTypes"
          :disabled="areCategoriesDisabled"
          @update="updatePushCategory"
        />

        <CategorySection
          :title="localize('profile.my.notification.category.check_ins')"
          :category="NotificationCategory.CHECK_INS"
          :preference="pushPreferences[NotificationCategory.CHECK_INS]"
          :sub-type-labels="checkInSubTypes"
          :disabled="areCategoriesDisabled"
          @update="updatePushCategory"
        />

        <CategorySection
          :title="localize('profile.my.notification.category.chat')"
          :category="NotificationCategory.CHAT"
          :preference="pushPreferences[NotificationCategory.CHAT]"
          :sub-type-labels="{}"
          :simple="true"
          :disabled="areCategoriesDisabled"
          @update="updatePushCategory"
        />

        <CategorySection
          :title="localize('profile.my.notification.category.new_followers')"
          :category="NotificationCategory.NEW_FOLLOWERS"
          :preference="pushPreferences[NotificationCategory.NEW_FOLLOWERS]"
          :sub-type-labels="{}"
          :simple="true"
          :disabled="areCategoriesDisabled"
          @update="updatePushCategory"
        />

        <CategorySection
          :title="localize('profile.my.notification.category.handshakes')"
          :category="NotificationCategory.HANDSHAKES"
          :preference="pushPreferences[NotificationCategory.HANDSHAKES]"
          :sub-type-labels="{}"
          :simple="true"
          :disabled="areCategoriesDisabled"
          @update="updatePushCategory"
        />
      </div>
    </div>
  </DrawerModal>
</template>

<script setup lang="ts">
import {
  type CategoryPreference,
  getDefaultEmailPreferences,
  getDefaultPushPreferences,
  NotificationCategory,
  type NotificationPreferences,
  NotificationSubType,
  SmsCategory,
} from '@/core/data/user/user.interface';
import { getPushNotificationToken, isPushPermissionGranted } from '@/core/domain/notification/pushNotifications';
import { gambits, localize } from '@/core/gambits';
import { useAuthStore } from '@/stores/auth.store';
import DrawerModal from '@/views/components/DrawerModal.vue';
import GamSwitch from '@/views/components/GamSwitch.vue';
import { GamSwitchPosition, GamSwitchValueType } from '@/views/composables/constants/components/gamSwitch.constants';
import { NotificationTopics } from '@/views/composables/constants/gam.constants';
import type { GamSwitchType } from '@/views/composables/models/components/GamSwitch';
import { watchDeep } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';
import { useToast } from 'vue-toastification';
import CategorySection from './components/CategorySection.vue';

interface NotificationPreferencesProps {
  isOpen: boolean;
}

defineProps<NotificationPreferencesProps>();
const emit = defineEmits<{ close: [] }>();

const handleClose = () => {
  hydrateFromUserDetails();
  emit('close');
};

const authStore = useAuthStore();
const { userDetails } = storeToRefs(authStore);

const isSystemPushEnabled = ref<boolean>(!('Notification' in window) || Notification.permission !== 'denied');

const readMergedPushFromStore = (): Record<NotificationCategory, CategoryPreference> => {
  const raw = userDetails.value?.notificationPreferences?.push;
  const defaults = getDefaultPushPreferences();
  const merged = { ...defaults, ...raw };
  const out = {} as Record<NotificationCategory, CategoryPreference>;
  for (const cat of Object.values(NotificationCategory)) {
    const m = merged[cat] ?? defaults[cat];
    out[cat] = { enabled: m.enabled, mode: m.mode, subTypes: [...(m.subTypes ?? [])] };
  }

  return out;
};

const isHydrating = ref(false);
const pushPreferences = reactive<Record<NotificationCategory, CategoryPreference>>(readMergedPushFromStore());

const isPushDeliveryEnabled = ref<boolean>(
  isPushPermissionGranted() && userDetails.value?.notificationPreferences?.push !== undefined,
);

const initialDeliverPush = ref<boolean>(false);

const isLoading = ref<boolean>(false);
const isFormChanged = ref<boolean>(false);

const hydrateFromUserDetails = () => {
  isHydrating.value = true;
  try {
    const nextPush = readMergedPushFromStore();
    for (const cat of Object.values(NotificationCategory)) {
      const n = nextPush[cat];
      pushPreferences[cat] = { enabled: n.enabled, mode: n.mode, subTypes: [...n.subTypes] };
    }

    const notDenied = !('Notification' in window) || Notification.permission !== 'denied';
    isPushDeliveryEnabled.value =
      isPushPermissionGranted() && userDetails.value?.notificationPreferences?.push !== undefined;

    initialDeliverPush.value = isPushDeliveryEnabled.value && isPushPermissionGranted();
    isSystemPushEnabled.value = notDenied;

    isFormChanged.value = false;
  } finally {
    void nextTick(() => {
      isHydrating.value = false;
    });
  }
};

watch(
  () => userDetails.value?.notificationPreferences,
  () => {
    hydrateFromUserDetails();
  },
  { deep: true, immediate: true },
);

const irlPlayConfirmedSubTypes: Record<string, string> = {
  [NotificationSubType.WHERE_I_PLAY]: localize('profile.my.notification.subtype.where_i_play'),
  [NotificationSubType.FOLLOWED_PLAYER]: localize('profile.my.notification.subtype.followed_player'),
  [NotificationSubType.SIMILAR_EXPERIENCE]: localize('profile.my.notification.subtype.similar_experience'),
  [NotificationSubType.FOLLOWED_SPOT]: localize('profile.my.notification.subtype.followed_spot'),
};

const irlPlayChangedSubTypes: Record<string, string> = {
  [NotificationSubType.TIME_OR_SPOT_CHANGED]: localize('profile.my.notification.subtype.time_or_spot_changed'),
  [NotificationSubType.COMPROMISED]: localize('profile.my.notification.subtype.compromised'),
  [NotificationSubType.CANCELED]: localize('profile.my.notification.subtype.canceled'),
};

const challengeSubTypes: Record<string, string> = {
  [NotificationSubType.CHALLENGE_RECEIVED]: localize('profile.my.notification.subtype.challenge_received'),
  [NotificationSubType.CHALLENGE_ACCEPTED]: localize('profile.my.notification.subtype.challenge_accepted'),
  [NotificationSubType.CHALLENGE_UPDATED]: localize('profile.my.notification.subtype.challenge_updated'),
  [NotificationSubType.CHALLENGE_DECLINED]: localize('profile.my.notification.subtype.challenge_declined'),
  [NotificationSubType.CHALLENGE_WITHDRAWN]: localize('profile.my.notification.subtype.challenge_withdrawn'),
};

const invitationSubTypes: Record<string, string> = {
  [NotificationSubType.INVITATION_RECEIVED]: localize('profile.my.notification.subtype.invitation_received'),
  [NotificationSubType.INVITATION_ACCEPTED]: localize('profile.my.notification.subtype.invitation_accepted'),
  [NotificationSubType.INVITATION_DECLINED]: localize('profile.my.notification.subtype.invitation_declined'),
};

const irlPlayProposedSubTypes: Record<string, string> = {
  [NotificationSubType.WHERE_I_PLAY_PROPOSED]: localize('profile.my.notification.subtype.where_i_play'),
  [NotificationSubType.WHERE_I_AM_AVAILABLE]: localize('profile.my.notification.subtype.where_i_am_available'),
};

const checkInSubTypes: Record<string, string> = {
  [NotificationSubType.FOLLOWED_PLAYER]: localize('profile.my.notification.subtype.checkin_followed_player'),
  [NotificationSubType.SIMILAR_EXPERIENCE]: localize('profile.my.notification.subtype.checkin_similar_experience'),
  [NotificationSubType.FOLLOWED_SPOT]: localize('profile.my.notification.subtype.checkin_followed_spot'),
};

const boardsSubTypes: Record<string, string> = {
  [NotificationSubType.MOVE_ON_MY_POSITION]: localize('profile.my.notification.category.boards.move_on_my_position'),
  [NotificationSubType.BOARD_COMPLETED]: localize('profile.my.notification.category.boards.board_completed'),
};

watchDeep(pushPreferences, () => {
  if (isHydrating.value) return;
  isFormChanged.value = true;
});

const isSavingToggle = ref(false);
const areCategoriesDisabled = computed(() => !isPushDeliveryEnabled.value || isSavingToggle.value);

watch(isPushDeliveryEnabled, async (enabled) => {
  if (isHydrating.value) return;
  if (isSavingToggle.value) return;

  isSavingToggle.value = true;
  isLoading.value = true;
  try {
    const saved = await savePushToggle(enabled).catch(() => false);
    if (!saved) {
      isPushDeliveryEnabled.value = !enabled;
      // Let this watcher see the revert while the guard is still up, so it isn't saved back.
      await nextTick();
    }
  } finally {
    isSavingToggle.value = false;
    isLoading.value = false;
  }
});

const savePushToggle = async (enabled: boolean): Promise<boolean> => {
  const prefsSnapshot = userDetails.value?.notificationPreferences;
  const emailFromStore = prefsSnapshot?.email
    ? { ...getDefaultEmailPreferences(), ...prefsSnapshot.email }
    : getDefaultEmailPreferences();

  if (enabled) {
    const token = await getPushNotificationToken();
    if (typeof token !== 'string' || token.length === 0) {
      useToast().error(localize('profile.my.notification.enable_failed'));
      return false;
    }

    // Token first: push preferences without a stored token would read as "on" but deliver nothing.
    const stored = await gambits.userService.storeNotificationToken(token, [NotificationTopics.GAMBIT]);
    if (stored.error || !stored.data) {
      useToast().error(localize('profile.my.notification.push_token_error'));
      return false;
    }

    const saved = await authStore.updateNotificationPreferences({
      email: emailFromStore,
      sms: prefsSnapshot?.sms ?? { [SmsCategory.SPARKS]: false },
      push: pushPreferences as Record<NotificationCategory, CategoryPreference>,
    });
    if (!saved) {
      useToast().error(localize('profile.my.notification.save_error'));
      return false;
    }

    initialDeliverPush.value = true;
    return true;
  }

  const saved = await authStore.updateNotificationPreferences({
    email: emailFromStore,
    sms: prefsSnapshot?.sms ?? { [SmsCategory.SPARKS]: false },
  });
  if (!saved) {
    useToast().error(localize('profile.my.notification.save_error'));
    return false;
  }

  initialDeliverPush.value = false;

  // Push is already off once the preferences drop it; stale tokens only waste sends.
  const deactivated = await gambits.userService.deactivateAllNotificationTokens();
  if (deactivated.error || !deactivated.data) {
    useToast().error(localize('profile.my.notification.save_error'));
  }

  return true;
};

let permissionStatus: PermissionStatus | null = null;

const onPermissionChange = () => {
  if (!permissionStatus) return;
  const denied = permissionStatus.state === 'denied';
  isSystemPushEnabled.value = !denied;
  if (denied) {
    isPushDeliveryEnabled.value = false;
  }
};

onMounted(async () => {
  try {
    if (navigator.permissions) {
      permissionStatus = await navigator.permissions.query({ name: 'notifications' as PermissionName });
      isSystemPushEnabled.value = permissionStatus.state !== 'denied';
      permissionStatus.addEventListener('change', onPermissionChange);
    }
  } catch {
    // Permissions API not supported
  }
});

onBeforeUnmount(() => {
  if (permissionStatus) {
    permissionStatus.removeEventListener('change', onPermissionChange);
    permissionStatus = null;
  }
});

const updatePushCategory = (category: NotificationCategory, preference: CategoryPreference) => {
  pushPreferences[category] = preference;
};

const saveNotifications = async () => {
  if (!isFormChanged.value) {
    emit('close');
    return;
  }

  isLoading.value = true;

  const prefsSnapshot = userDetails.value?.notificationPreferences;
  const emailFromStore = prefsSnapshot?.email
    ? { ...getDefaultEmailPreferences(), ...prefsSnapshot.email }
    : getDefaultEmailPreferences();
  const deliverPush = isPushDeliveryEnabled.value && isPushPermissionGranted();
  const notificationPreferences: NotificationPreferences = {
    email: emailFromStore,
    sms: prefsSnapshot?.sms ?? { [SmsCategory.SPARKS]: false },
    ...(deliverPush ? { push: pushPreferences as Record<NotificationCategory, CategoryPreference> } : {}),
  };

  try {
    const saved = await authStore.updateNotificationPreferences(notificationPreferences);
    if (!saved) {
      useToast().error(localize('profile.my.notification.save_error'));
      isLoading.value = false;

      return;
    }

    isFormChanged.value = false;
    emit('close');
  } catch (e) {
    useToast().error(localize('profile.my.notification.save_error'));
    // eslint-disable-next-line no-console
    console.error('Failed to save notifications', e);
  }
  isLoading.value = false;
};

const pushEnabled = (): GamSwitchType => ({
  label: 'profile.my.notification.push',
  name: 'profile.my.notification.push',
  type: GamSwitchValueType.SWITCH,
  position: GamSwitchPosition.RIGHT,
});
</script>

<style scoped lang="scss">
.profile-notifications {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-l);

  .system-disabled-notice {
    font-size: 13px;
    color: var(--color-error, #ff0000);
    padding: var(--spacing-sm) 0;
  }

  .general-sections {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: var(--spacing-l);
    align-self: stretch;
  }
}
</style>
